import { 
  signInWithPopup, 
  signInWithRedirect, 
  getRedirectResult, 
  browserPopupRedirectResolver,
  OAuthProvider,
  UserCredential
} from 'firebase/auth';
import { auth } from '../firebase';
import { UserService } from './userService';
import { markRedirectPending, takeRedirectPending } from '../utils/authRedirectPending';

/**
 * Standard Firebase Microsoft OAuth implementation
 * Using Firebase's built-in OAuth flow instead of custom implementation
 */
export class MicrosoftAuthStandard {
  private static provider: OAuthProvider | null = null;

  /**
   * Initialize and configure the Microsoft OAuth provider
   */
  private static getProvider(): OAuthProvider {
    if (!this.provider) {
      this.provider = new OAuthProvider('microsoft.com');
      
      // Set custom parameters
      this.provider.setCustomParameters({
        // Force account selection
        prompt: 'select_account',
        // Use 'common' for any Microsoft account (personal or work/school)
        // Or use your specific tenant ID for Azure AD accounts only
        tenant: 'common'
      });

      // Add required scopes
      this.provider.addScope('openid');
      this.provider.addScope('profile');
      this.provider.addScope('email');
      
      // Optional: Add additional scopes if needed
      // this.provider.addScope('offline_access'); // For refresh token
    }
    return this.provider;
  }

  /**
   * Sign in with Microsoft using popup (recommended for desktop)
   */
  static async signInWithPopup(): Promise<UserCredential> {
    try {
      console.log('Starting Microsoft sign-in with popup...');
      const provider = this.getProvider();
      
      // Sign in with popup
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      
      // Extract OAuth credentials
      const credential = OAuthProvider.credentialFromResult(result);
      console.log('Microsoft sign-in successful:', {
        user: result.user.email,
        providerId: credential?.providerId,
        hasAccessToken: !!credential?.accessToken,
        hasIdToken: !!credential?.idToken
      });

      // Create or update user in Firestore
      try {
        await UserService.getOrCreateUser(result.user);
        console.log('User account created/updated in Firestore');
      } catch (error) {
        console.error('Failed to create/update user in Firestore:', error);
        // Don't fail auth if Firestore fails
      }

      return result;
    } catch (error: any) {
      console.error('Microsoft popup sign-in failed:', {
        code: error.code,
        message: error.message,
        // No email: never log a reader's full address (CLAUDE.md invariant 10).
      });
      
      // Handle specific errors
      // `auth/account-exists-with-different-credential` is passed through
      // untouched: AuthContext recognises it by its CODE and shows the shared
      // message ([[BUGS #429]]). Wrapping it in a plain Error dropped the code,
      // so a Microsoft clash used to read "Failed to sign in with Microsoft".
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        // Fallback to redirect if popup is blocked
        console.log('Popup blocked, falling back to redirect...');
        return this.signInWithRedirect();
      }
      
      throw error;
    }
  }

  /**
   * Sign in with Microsoft using redirect (recommended for mobile)
   */
  static async signInWithRedirect(): Promise<any> {
    try {
      console.log('Starting Microsoft sign-in with redirect...');
      const provider = this.getProvider();
      
      // This will redirect the user to Microsoft's sign-in page
      markRedirectPending();
      await signInWithRedirect(auth, provider, browserPopupRedirectResolver);
      
      // Note: This function returns immediately
      // The result will be handled by handleRedirectResult after redirect
    } catch (error: any) {
      console.error('Microsoft redirect sign-in failed:', error);
      throw error;
    }
  }

  /**
   * Handle the result after redirect sign-in
   * Call this on app initialization to handle redirect results
   */
  static async handleRedirectResult(): Promise<UserCredential | null> {
    try {
      // Only when a redirect was started in this tab: asking loads the auth
      // iframe, which an anonymous reader should never pay for (BUGS #401).
      if (!takeRedirectPending()) return null;
      console.log('Checking for redirect result...');
      
      // Get the redirect result
      const result = await getRedirectResult(auth, browserPopupRedirectResolver);
      
      if (result) {
        // Extract OAuth credentials
        const credential = OAuthProvider.credentialFromResult(result);
        console.log('Microsoft redirect sign-in successful:', {
          // Never the address itself (invariant 10).
          hasEmail: !!result.user.email,
          providerId: credential?.providerId,
          hasAccessToken: !!credential?.accessToken,
          hasIdToken: !!credential?.idToken
        });

        // Create or update user in Firestore
        try {
          await UserService.getOrCreateUser(result.user);
          console.log('User account created/updated in Firestore');
        } catch (error) {
          console.error('Failed to create/update user in Firestore:', error);
          // Don't fail auth if Firestore fails
        }

        return result;
      }
      
      // No redirect result (user didn't come from redirect)
      return null;
    } catch (error: any) {
      console.error('Error handling redirect result:', {
        code: error.code,
        message: error.message
      });
      
      // Handle specific errors
      throw error;
    }
  }

  /**
   * Check if we should handle a redirect result
   * Useful to call on app initialization
   */
  static async checkAndHandleRedirect(): Promise<boolean> {
    try {
      const result = await this.handleRedirectResult();
      return result !== null;
    } catch (error) {
      console.error('Failed to handle redirect:', error);
      return false;
    }
  }
}